<script setup>
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from '@/stores/meta.js'
import { api } from '@/lib/http.js'
import { ENTITIES, entityPath } from '@/lib/entities.js'
import { reportError } from '@/composables/useSave.js'
import RefPicker from '@/components/form/RefPicker.vue'
import OwnerPicker from '@/components/form/OwnerPicker.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiAvatar from '@/components/ui/UiAvatar.vue'
import RelatedActions from './RelatedActions.vue'
import { useUi } from '@/stores/ui.js'
import { useSession } from '@/stores/session.js'

/**
 * Links with a role (D-39): an account's contacts, a contact's accounts, an opportunity's
 * stakeholders, competitors and sales team. Each link is written to both records' history.
 * As every section of related records: "New" makes one in the panel from the right and links
 * it once saved, "Link existing" finds one (the team: people of the company, linked only).
 */
const props = defineProps({
  entity: { type: String, required: true },
  record: { type: Object, required: true },
  relation: { type: String, required: true }, // contacts | accounts | stakeholders | competitors | team | children
  canEdit: Boolean,
  required: Boolean, // a required stage step: marked in the header
})
const emit = defineEmits(['changed'])
const { t } = useI18n()
const meta = useMeta()
const adding = ref(false)
const ui = useUi()
const session = useSession()
const target = ref(null)
const role = ref('')
const influence = ref('')
const busy = ref(false)

const roleText = (dictionary, key) => (key ? meta.dictionaryLabel(dictionary, key) : '')
const config = computed(() => ({
  contacts: { title: t('record.relatedContacts'), target: 'contact', roles: 'contact_role', items: (props.record.contacts || []).map((c) => ({ uuid: c.uuid, label: c.label, entity: 'contact', sub: [roleText('contact_role', c.role), c.job_title, c.is_primary ? t('record.primary') : ''].filter(Boolean).join(' · ') })) },
  accounts: { title: t('record.relatedAccounts'), target: 'account', roles: 'contact_role', items: (props.record.accounts || []).map((a) => ({ uuid: a.uuid, label: a.label, entity: 'account', sub: [roleText('contact_role', a.role), a.job_title, a.is_primary ? t('record.primary') : ''].filter(Boolean).join(' · ') })) },
  stakeholders: { title: t('record.stakeholders'), target: 'contact', roles: 'stakeholder_role', influence: true, items: (props.record.stakeholders || []).map((s) => ({ uuid: s.contact?.uuid, label: s.contact?.label, entity: 'contact', sub: [roleText('stakeholder_role', s.role), s.influence ? `${t('record.influence')}: ${roleText('stakeholder_influence', s.influence)}` : ''].filter(Boolean).join(' · ') })) },
  competitors: { title: t('record.competitors'), target: 'competitor', items: (props.record.competitors || []).map((c) => ({ uuid: c.uuid, label: c.label, entity: 'competitor', sub: c.note || '' })) },
  team: { title: t('record.team'), target: 'user', roles: 'team_role', items: (props.record.team || []).map((m) => ({ uuid: m.user?.uuid, label: m.user?.label, user: true, sub: roleText('team_role', m.role) })) },
  children: { title: t('record.children'), target: 'account', items: (props.record.children || []).map((c) => ({ uuid: c.uuid, label: c.label, entity: 'account', sub: '' })) },
}[props.relation]))

async function link() {
  if (!target.value) return
  busy.value = true
  try {
    const body = {}
    if (role.value) body.role = role.value
    if (influence.value) body.influence = influence.value
    await api(`/${entityPath(props.entity)}/${props.record.uuid}/${props.relation}/${target.value}`, { method: 'POST', body })
    adding.value = false
    target.value = null
    role.value = ''
    influence.value = ''
    emit('changed')
  } catch (e) {
    reportError(e)
  } finally {
    busy.value = false
  }
}
// A new record for the section, made from what this one knows; linked once saved, unless making
// it with this record already links it (a contact made with its account, a child with its parent).
const NEW = {
  contacts: () => ({ entity: 'contact', defaults: { account_uuid: props.record.uuid, _refs: { account: props.record } }, linked: true }),
  accounts: () => ({ entity: 'account', defaults: {} }),
  stakeholders: () => ({ entity: 'contact', defaults: props.record.account_uuid ? { account_uuid: props.record.account_uuid, _refs: { account: props.record.account } } : {} }),
  competitors: () => ({ entity: 'competitor', defaults: {} }),
  children: () => ({ entity: 'account', defaults: { parent_account_uuid: props.record.uuid, _refs: { parent_account: props.record } }, linked: true }),
}
const canCreate = computed(() => props.canEdit && !!NEW[props.relation] && session.has(`${NEW[props.relation]().entity}:create`))
function create() {
  const made = NEW[props.relation]()
  ui.openCreate({
    entity: made.entity,
    defaults: made.defaults,
    from: props.record.label,
    onSaved: async (record) => {
      if (!made.linked && record?.uuid) {
        try {
          await api(`/${entityPath(props.entity)}/${props.record.uuid}/${props.relation}/${record.uuid}`, { method: 'POST', body: {} })
        } catch (e) {
          reportError(e)
        }
      }
      emit('changed')
    },
  })
}
async function unlink(item) {
  // Asked first: one slip of the mouse would undo a link someone made.
  if (!(await ui.ask({ title: t('related.detachTitle'), message: t('related.detachLinkMessage', { record: item.label, from: props.record.label }), confirmLabel: t('related.detach') }))) return
  try {
    await api(`/${entityPath(props.entity)}/${props.record.uuid}/${props.relation}/${item.uuid}`, { method: 'DELETE' })
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
</script>

<template>
  <section class="card" :data-testid="`links-${relation}`">
    <header class="flex items-center gap-2 border-b border-default px-3 py-2">
      <i v-if="ENTITIES[config.target]" :class="['fa-solid text-sm', ENTITIES[config.target].icon, ENTITIES[config.target].color]" aria-hidden="true"></i>
      <i v-else class="fa-solid fa-users text-sm text-faint" aria-hidden="true"></i>
      <h3 class="flex-1 text-sm font-semibold">{{ config.title }}<span v-if="required" class="ml-0.5 text-red-600" aria-hidden="true">*</span> <span class="font-normal text-faint">({{ config.items.length }})</span></h3>
      <RelatedActions :can-create="canCreate" :can-link="canEdit" :linking="adding" :testid="`links-${relation}`" @create="create" @link="adding = !adding" />
    </header>
    <div v-if="adding" class="space-y-2 border-b border-default p-3">
      <RefPicker v-if="config.target === 'contact' || config.target === 'account' || config.target === 'competitor'" v-model="target" :entity="config.target" :data-testid="config.target === 'competitor' ? 'competitor-picker' : undefined" />
      <OwnerPicker v-else :model-value="null" users-only @update:model-value="(o) => (target = o?.uuid || null)" />
      <select v-if="config.roles" v-model="role" class="input">
        <option value="">{{ $t('record.role') }}…</option>
        <option v-for="e in meta.dictionary(config.roles).entries" :key="e.key" :value="e.key">{{ meta.localized(e.label) }}</option>
      </select>
      <select v-if="config.influence" v-model="influence" class="input">
        <option value="">{{ $t('record.influence') }}…</option>
        <option v-for="e in meta.dictionary('stakeholder_influence').entries" :key="e.key" :value="e.key">{{ meta.localized(e.label) }}</option>
      </select>
      <div class="flex justify-end gap-2">
        <UiButton size="sm" @click="adding = false">{{ $t('common.cancel') }}</UiButton>
        <UiButton size="sm" variant="primary" icon="fa-link" :disabled="!target" :loading="busy" :data-testid="`links-${relation}-attach`" @click="link">{{ $t('related.attach') }}</UiButton>
      </div>
    </div>
    <p v-if="!config.items.length" class="px-3 py-3 text-sm text-faint">{{ $t('common.empty') }}</p>
    <ul v-else class="divide-y divide-[var(--border)]">
      <li v-for="item in config.items" :key="item.uuid" class="flex items-center gap-2 px-3 py-2">
        <UiAvatar v-if="item.user" :label="item.label || ''" size="sm" />
        <div class="min-w-0 flex-1">
          <RecordLink v-if="item.entity" :entity="item.entity" :uuid="item.uuid" class="block truncate text-sm font-medium hover:underline">{{ item.label }}</RecordLink>
          <p v-else class="truncate text-sm font-medium">{{ item.label }}</p>
          <p v-if="item.sub" class="truncate text-xs text-muted">{{ item.sub }}</p>
        </div>
        <UiButton v-if="canEdit" size="xs" variant="ghost" icon="fa-link-slash" class="shrink-0 opacity-60 hover:text-red-600 hover:opacity-100" :title="$t('related.detach')" :aria-label="$t('related.detach')" @click="unlink(item)" />
      </li>
    </ul>
  </section>
</template>
